import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Clock, Home, MapPin, Phone, Receipt, ShoppingBag, User, UtensilsCrossed } from 'lucide-react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/hooks';
import { CartProvider, useCart } from '../../lib/cart';
import { useAuth } from '../../lib/auth';
import { ErrorBox, PageLoading } from '../../components/ui';
import { InstallButton } from '../../components/InstallButton';
import { hoursRange, LangToggle, useI18n } from '../../i18n';
import { PublicCtx, type Menu, type PublicRestaurant } from './context';
import { WEEKDAYS, WEEKDAY_LABELS } from '@pizza-roma/shared';

export function PublicLayout() {
  const { slug = '' } = useParams();
  const { t } = useI18n();
  const { data, error, loading, reload } = useAsync(
    async () => {
      const [restaurant, menu] = await Promise.all([api.get<PublicRestaurant>(`/api/restaurants/${slug}`), api.get<Menu>(`/api/restaurants/${slug}/menu`)]);
      document.title = restaurant.name;
      return { restaurant, menu };
    },
    [slug],
  );
  if (loading && !data) return <PageLoading />;
  if (error || !data)
    return (
      <div className="pub-main">
        <ErrorBox error={error ?? new Error(t.pub.restaurantNotFound)} onRetry={reload} />
      </div>
    );
  return (
    <PublicCtx.Provider value={data}>
      <CartProvider slug={slug}>
        <Header restaurant={data.restaurant} />
        <Outlet />
        <Footer restaurant={data.restaurant} />
        <BottomNav slug={slug} />
      </CartProvider>
    </PublicCtx.Provider>
  );
}

function Header({ restaurant }: { restaurant: PublicRestaurant }) {
  const { count } = useCart();
  const { me } = useAuth();
  const { t } = useI18n();
  const base = `/r/${restaurant.slug}`;
  return (
    <header className="pub-header">
      <div className="pub-header-inner">
        <Link to={base} className="pub-logo" aria-label={restaurant.name}>
          {restaurant.logoUrl ? <img src={restaurant.logoUrl.replace('-transparent', '')} alt={restaurant.name} /> : <b style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>{restaurant.name}</b>}
        </Link>
        <nav className="pub-nav">
          <NavLink to={base} end>
            {t.pub.navHome}
          </NavLink>
          <NavLink to={`${base}/menu`}>{t.pub.navMenu}</NavLink>
          <a href="#a-propos">{t.pub.navAbout}</a>
          <a href="#nous-joindre">{t.pub.navContact}</a>
        </nav>
        <div className="pub-actions">
          <LangToggle dark />
          <InstallButton className="btn btn-outline-light btn-sm hide-sm" label={t.install.short} appName={restaurant.name} />
          <Link to={`${base}/account`} className="btn btn-outline-light btn-sm" aria-label={t.pub.myAccount}>
            <User size={18} /> <span className="hide-xs">{me?.user ? me.user.displayName.split(' ')[0] : t.pub.signIn}</span>
          </Link>
          <Link to={`${base}/cart`} className="btn btn-primary btn-sm cart-btn" aria-label={t.pub.cart} data-testid="header-cart">
            <ShoppingBag size={18} />
            {count > 0 && <span className="cart-count">{count}</span>}
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer({ restaurant }: { restaurant: PublicRestaurant }) {
  const { t, lang, demo } = useI18n();
  return (
    <footer className="pub-footer" id="nous-joindre">
      <div className="pub-footer-inner">
        <div id="a-propos" className="stack">
          <h4>{restaurant.name}</h4>
          <p>{demo(restaurant.description)}</p>
          <p className="row">
            <MapPin size={16} /> {restaurant.address.line1}, {restaurant.address.city}
          </p>
          <p className="row">
            <Phone size={16} /> <a href={`tel:${restaurant.phone}`}>{restaurant.phone}</a>
          </p>
        </div>
        <div>
          <h4>
            <Clock size={15} style={{ verticalAlign: '-2px' }} /> {t.pub.openingHours}
          </h4>
          {WEEKDAYS.map((d) => (
            <div key={d} className="row-between small">
              <span>{WEEKDAY_LABELS[lang][d]}</span>
              <span>{restaurant.hours[d]?.closed ? t.common.closed : hoursRange(restaurant.hours[d]?.open, restaurant.hours[d]?.close, lang)}</span>
            </div>
          ))}
        </div>
        <div className="stack">
          <h4>{t.pub.appTitle}</h4>
          <p>{t.pub.appPitch(restaurant.name)}</p>
          <InstallButton className="btn btn-outline-light" appName={restaurant.name} />
          <Link to="/login" className="small">
            {t.pub.staffArea}
          </Link>
        </div>
      </div>
    </footer>
  );
}

function BottomNav({ slug }: { slug: string }) {
  const { count } = useCart();
  const nav = useNavigate();
  const { t } = useI18n();
  const base = `/r/${slug}`;
  return (
    <nav className="bottom-nav" aria-label={t.pub.navigation}>
      <NavLink to={base} end>
        <Home size={22} />
        {t.pub.navHome}
      </NavLink>
      <NavLink to={`${base}/menu`}>
        <UtensilsCrossed size={22} />
        {t.pub.navMenu}
      </NavLink>
      <NavLink to={`${base}/cart`}>
        <ShoppingBag size={22} />
        {count > 0 && <span className="cart-count">{count}</span>}
        {t.pub.cart}
      </NavLink>
      <button onClick={() => nav(`${base}/account#commandes`)}>
        <Receipt size={22} />
        {t.pub.orders}
      </button>
      <NavLink to={`${base}/account`}>
        <User size={22} />
        {t.pub.account}
      </NavLink>
    </nav>
  );
}
